import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Empty,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  EyeOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import type {
  ExtractionJobStatus,
  LatestExtractionJob,
  PageExtraction,
  ResourceFileResponse,
} from "../../../types/api/getResources";
import { triggerResourceExtraction } from "../../../services/resources/resources.service";
import { useNotification } from "../../../hooks/useNotification";

const POLLING_INTERVAL_MS = 5000;

const { Title, Text, Paragraph } = Typography;

interface ExtractionTabProps {
  resourceId: number;
  job: LatestExtractionJob | null | undefined;
  files: ResourceFileResponse[] | undefined;
  onRefetch: () => Promise<void> | void;
}

const isJobActive = (status: ExtractionJobStatus | undefined) =>
  status === "PENDING" || status === "IN_PROGRESS";

const STATUS_COPY: Record<
  ExtractionJobStatus,
  { label: string; color: "blue" | "processing" | "success" | "error" }
> = {
  PENDING: { label: "En cola", color: "blue" },
  IN_PROGRESS: { label: "Procesando PDF", color: "processing" },
  COMPLETED: { label: "Completado", color: "success" },
  FAILED: { label: "Fallido", color: "error" },
};

const buildDisplayName = (
  file: ResourceFileResponse,
  sameTypeIndex: number,
  sameTypeTotal: number,
): string => {
  switch (file.file_type) {
    case "TEACHER_BOOK":
      return "Libro del profesor";
    case "STUDENT_BOOK":
      return "Libro del alumno";
    case "MAIN_FILE":
      return sameTypeTotal > 1
        ? `PDF ${sameTypeIndex + 1}`
        : "PDF";
    default:
      return sameTypeTotal > 1
        ? `Archivo ${sameTypeIndex + 1}`
        : "Archivo";
  }
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const StatusBanner = ({ job }: { job: LatestExtractionJob }) => {
  const copy = STATUS_COPY[job.status];

  if (job.status === "PENDING" || job.status === "IN_PROGRESS") {
    return (
      <Alert
        type="info"
        showIcon
        icon={<Spin size="small" />}
        message={`${copy.label}…`}
        description={`Intento ${job.attempt_count} · actualizado ${formatDate(
          job.updated_at,
        )}`}
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (job.status === "FAILED") {
    return (
      <Alert
        type="error"
        showIcon
        message="La extracción falló"
        description={
          <>
            {job.error_message && (
              <Paragraph style={{ marginBottom: 4 }}>
                {job.error_message}
              </Paragraph>
            )}
            <Text type="secondary">
              Intentos: {job.attempt_count} · {formatDate(job.updated_at)}
            </Text>
          </>
        }
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <Alert
      type="success"
      showIcon
      message={`Extracción completada (${job.attempt_count} ${
        job.attempt_count === 1 ? "intento" : "intentos"
      })`}
      description={`Última actualización: ${formatDate(job.updated_at)}`}
      style={{ marginBottom: 16 }}
    />
  );
};

const PageRow = ({
  page,
  onView,
}: {
  page: PageExtraction;
  onView: (page: PageExtraction) => void;
}) => {
  const isError = !!page.error_message;
  const canView = !!page.extracted_content;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        borderBottom: "1px solid var(--bo-card-border)",
      }}
    >
      <Space size="middle">
        <Text>Página {page.page_number}</Text>
        {isError ? (
          <Tooltip title={page.error_message ?? ""}>
            <Badge status="warning" text="Con error" />
          </Tooltip>
        ) : (
          <Badge status="success" text="OK" />
        )}
      </Space>
      <Button
        type="link"
        size="small"
        icon={<EyeOutlined />}
        disabled={!canView}
        onClick={() => onView(page)}
      >
        Ver contenido
      </Button>
    </div>
  );
};

const FileCard = ({
  file,
  displayName,
  onView,
}: {
  file: ResourceFileResponse;
  displayName: string;
  onView: (
    page: PageExtraction,
    file: ResourceFileResponse,
    displayName: string,
  ) => void;
}) => {
  const pages = file.pageExtractions ?? [];
  const errored = pages.filter((p) => p.error_message !== null).length;
  const ok = pages.length - errored;

  return (
    <div
      style={{
        border: "1px solid var(--bo-border-strong)",
        borderRadius: 8,
        marginBottom: 16,
        background: "var(--bo-card-bg)",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--bo-card-border)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <FilePdfOutlined style={{ color: "#cf1322", fontSize: 18 }} />
        <Text strong>{displayName}</Text>
        <Tag color="green">{ok} OK</Tag>
        {errored > 0 && <Tag color="orange">{errored} con error</Tag>}
        <Text type="secondary" style={{ marginLeft: "auto" }}>
          {pages.length} páginas
        </Text>
      </div>

      {pages.length === 0 ? (
        <div style={{ padding: 16 }}>
          <Text type="secondary">Sin extracción para este archivo.</Text>
        </div>
      ) : (
        <div>
          {pages.map((p) => (
            <PageRow
              key={p.page_number}
              page={p}
              onView={(page) => onView(page, file, displayName)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ExtractionTab = ({
  resourceId,
  job,
  files,
  onRefetch,
}: ExtractionTabProps) => {
  const [openPage, setOpenPage] = useState<{
    page: PageExtraction;
    file: ResourceFileResponse;
    displayName: string;
  } | null>(null);
  const [triggering, setTriggering] = useState(false);
  const { notify } = useNotification();
  const onRefetchRef = useRef(onRefetch);

  useEffect(() => {
    onRefetchRef.current = onRefetch;
  }, [onRefetch]);

  const filesWithExtraction = useMemo(() => {
    const eligible = (files ?? []).filter(
      (f) => (f.pageExtractions ?? []).length > 0,
    );
    const totalsByType = eligible.reduce<Record<string, number>>((acc, f) => {
      acc[f.file_type] = (acc[f.file_type] ?? 0) + 1;
      return acc;
    }, {});
    const seenByType: Record<string, number> = {};
    return eligible.map((file) => {
      const idx = seenByType[file.file_type] ?? 0;
      seenByType[file.file_type] = idx + 1;
      return {
        file,
        displayName: buildDisplayName(
          file,
          idx,
          totalsByType[file.file_type] ?? 1,
        ),
      };
    });
  }, [files]);

  const active = isJobActive(job?.status);

  // Auto-poll while the job is PENDING/IN_PROGRESS.
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      void onRefetchRef.current?.();
    }, POLLING_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [active]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const result = await triggerResourceExtraction(resourceId);
      if (result.ok) {
        notify(
          "success",
          `Extracción encolada (job #${result.data.job_id}). ${(result.data.replaced_file_ids ?? []).length} archivo(s) se re-procesarán.`,
          "Extracción encolada",
        );
        await onRefetchRef.current?.();
        return;
      }
      if (result.kind === "conflict") {
        notify(
          "warning",
          `Ya hay una extracción en curso (job #${result.active_job.id} · ${result.active_job.status}).`,
          "En curso",
        );
        await onRefetchRef.current?.();
        return;
      }
      const title =
        result.kind === "not_found"
          ? "Recurso no encontrado"
          : result.kind === "bad_request"
            ? "No admite extracción"
            : "Error";
      notify("error", result.message, title);
    } finally {
      setTriggering(false);
    }
  };

  if (!job && filesWithExtraction.length === 0) {
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12,
          }}
        >
          <Popconfirm
            title="¿Disparar una nueva extracción para este recurso?"
            okText="Sí, extraer"
            cancelText="Cancelar"
            onConfirm={handleTrigger}
          >
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={triggering}
            >
              Extraer contenido
            </Button>
          </Popconfirm>
        </div>
        <Empty
          description="Este recurso no tiene extracción de PDFs."
          style={{ padding: "32px 0" }}
        />
      </>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          gap: 12,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Estado de extracción
        </Title>
        <Popconfirm
          title="Re-extraer contenido"
          description="Se borrarán las extracciones previas de los archivos candidatos y se volverán a generar. ¿Continuar?"
          okText="Sí, re-extraer"
          cancelText="Cancelar"
          onConfirm={handleTrigger}
          disabled={active}
        >
          <Tooltip
            title={
              active
                ? "Hay una extracción en curso. Esperá a que termine."
                : undefined
            }
          >
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={triggering}
              disabled={active}
            >
              Re-extraer contenido
            </Button>
          </Tooltip>
        </Popconfirm>
      </div>

      {job ? (
        <StatusBanner job={job} />
      ) : (
        <Alert
          type="info"
          showIcon
          message="Sin extracción"
          description="No se ha encolado un job de extracción para este recurso."
          style={{ marginBottom: 16 }}
        />
      )}

      {filesWithExtraction.length === 0 ? (
        <Empty
          description="Aún no hay páginas extraídas."
          style={{ padding: "16px 0" }}
        />
      ) : (
        filesWithExtraction.map(({ file, displayName }) => (
          <FileCard
            key={file.id}
            file={file}
            displayName={displayName}
            onView={(page, f, name) =>
              setOpenPage({ page, file: f, displayName: name })
            }
          />
        ))
      )}

      <Modal
        open={!!openPage}
        onCancel={() => setOpenPage(null)}
        footer={null}
        width={900}
        title={
          openPage
            ? `${openPage.displayName} · Página ${openPage.page.page_number}`
            : ""
        }
      >
        {openPage?.page.error_message && (
          <Alert
            type="warning"
            showIcon
            message="Esta página tiene un error registrado"
            description={openPage.page.error_message}
            style={{ marginBottom: 12 }}
          />
        )}
        <pre
          style={{
            background: "var(--bo-inset-bg)",
            border: "1px solid var(--bo-card-border)",
            borderRadius: 6,
            padding: 12,
            maxHeight: "60vh",
            overflow: "auto",
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {openPage?.page.extracted_content ?? "Sin contenido extraído."}
        </pre>
        {openPage && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Actualizado: {formatDate(openPage.page.updated_at)}
          </Text>
        )}
      </Modal>
    </div>
  );
};

export default ExtractionTab;
